'use client';

import { DATADOG_RUM_NAME } from '@lichtblick/consent/ConsentManager/ConsentManager.types';
import { useConsent } from '@lichtblick/consent/hooks/useConsent';
import { FieldFooter } from '@lichtblick/photon/atoms/field-footer';
import { Text } from '@lichtblick/photon/atoms/text/text';
import { ExclamationMarkTriangleXs } from '@lichtblick/photon/icons/symbols/exclamation-mark-triangle-xs';
import { Checkbox } from '@lichtblick/photon/molecules/checkbox';
import { useRouter } from 'next/navigation';
import { type FC, useActionState, useEffect, useRef } from 'react';
import { SubmitButton } from '@/components/form-box/submit-button';
import { sessionStorageName } from '@/constants';
import { i18n } from '@/i18n';
import type { StepProps } from '@/lib/flow/types';
import { sanitizeStateForMonitoring } from '@/monitoring/sanitizeStateForMonitoring';
import styles from './overview-form.module.css';
import type { OverviewState } from './schema';

const trackRumLeadCreationEvent = (state: OverviewState) => {
  import('@datadog/browser-rum').then(({ datadogRum }) => {
    datadogRum.onReady(() =>
      datadogRum.addAction('create-energy-solution-lead', { state: sanitizeStateForMonitoring(state) }),
    );
  });
};

type OverviewFormProps = Pick<StepProps<OverviewState>, 'action' | 'completionPath' | 'state' | 'stepId'> & {
  isPersonalDataFormEditing: boolean;
  onSubmitAttempt: () => void;
  leadConsentText: React.ReactNode;
  marketingConsentText: React.ReactNode;
  privacyNoticeText: React.ReactNode;
};

export const OverviewForm: FC<OverviewFormProps> = ({
  action,
  completionPath,
  stepId,
  state,
  isPersonalDataFormEditing,
  onSubmitAttempt,
  leadConsentText,
  marketingConsentText,
  privacyNoticeText,
}) => {
  const [actionState, formAction, isPending] = useActionState(action, undefined);
  const router = useRouter();
  const hasHandledClientCompletion = useRef(false);

  const datadogRumConsent = useConsent(DATADOG_RUM_NAME);

  useEffect(() => {
    if (actionState?.success !== true || actionState.type !== 'client' || hasHandledClientCompletion.current) {
      return;
    }

    hasHandledClientCompletion.current = true;
    try {
      const serializedClientData = JSON.stringify(actionState.clientData);
      if (serializedClientData !== undefined) {
        sessionStorage.setItem(sessionStorageName.energySolutionsSubmitEvent, serializedClientData);
      }
    } catch {
      // Storage can be unavailable; still navigate to the completion page.
    }

    router.replace(completionPath);
  }, [actionState, completionPath, router]);

  return (
    <form
      className={styles.form}
      action={formAction}
      onSubmit={(e) => {
        if (isPersonalDataFormEditing) {
          e.preventDefault();
          onSubmitAttempt();
          return;
        }

        datadogRumConsent && trackRumLeadCreationEvent(state);
      }}
    >
      <input type="hidden" name="stepId" value={stepId} />

      <Checkbox
        data-testid="checkbox-isHouseOwner"
        errorMessage="Für die Entscheidung über bauliche Maßnahmen ist die Hauseigentümerschaft erforderlich."
        label="Ich bestätige, dass ich Eigentümer*in des Gebäudes bin, um das es geht.*"
      >
        <Checkbox.Checkbox defaultChecked={state.isHouseOwner === 'on'} name="isHouseOwner" required />
      </Checkbox>
      <Checkbox
        data-testid="checkbox-isLeadOptinGiven"
        errorMessage={i18n.validation.missing}
        label={<Text>{leadConsentText}*</Text>}
      >
        <Checkbox.Checkbox
          defaultChecked={state.isLeadOptinGiven === 'on'}
          name="isLeadOptinGiven"
          required
          value="on"
        />
      </Checkbox>

      <Checkbox
        data-testid="checkbox-isMarketingOptinGiven"
        errorMessage={i18n.validation.missing}
        label={<Text>{marketingConsentText}</Text>}
      >
        <Checkbox.Checkbox
          defaultChecked={state.isMarketingOptinGiven === 'on'}
          name="isMarketingOptinGiven"
          value="on"
        />
      </Checkbox>

      {privacyNoticeText}

      {actionState?.success === false &&
        actionState.errors.map((error, errorIndex) => (
          <FieldFooter
            key={`${error.code ?? 'error'}-${errorIndex}`}
            className={styles['error-message']}
            data-testid="field-error"
            error
            icon={<ExclamationMarkTriangleXs />}
          >
            {'message' in error && error.message ? error.message : i18n.validation.missing}
          </FieldFooter>
        ))}

      <Text className={styles.hint} size="xs">
        * Pflichtfelder
      </Text>
      <SubmitButton loading={isPending || actionState?.success} label="Kostenlos beraten lassen" />
    </form>
  );
};
